import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, catchError, of, throwError } from 'rxjs';
import { AppConfig } from 'src/app/configs/app.config';
import { HTTP_HEADER_NOT_LOADING, LS_ACCESS_TOKEN, LS_APP_TOKEN, LS_APP_TOKEN_API, LS_CURRENT_USER, LS_LAST_ACTIVITY, LS_REDIRECT_URL, LS_TOKEN_SCOPE, LS_USER_GROUP, LS_USER_MENU, LS_USER_ROLE } from 'src/app/constants/app.constant';
import Swal from 'sweetalert2';
import { StringUtilService } from './string-util.service';
import { AuthUtilService } from './auth-util.service';
import { AuthService } from '../authorization/auth.service';
import { Router } from '@angular/router';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root',
})
export class HttpUtilService {
  constructor(
    private http: HttpClient,
    private stringUtil: StringUtilService,
    // private authService: AuthService,
    private router: Router,
    private common: CommonService
  ) {}

  private storage = localStorage;

  get<T>(url: string, isLoading: boolean = AppConfig.LOADING) {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  getWithoutLoading<T>(url: string) {
    const httpOptions = this.generateRequestOptions({
      'x-NotShowLoading': 'true',
    });
    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  getWithQuery<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ) {
    let normalizeObject: { [param: string]: string | string[] } = {};
    this.normalizeParamsObject(body, normalizeObject);
    const params = new HttpParams({ fromObject: normalizeObject });
    let httpOptions = this.generateRequestOptions(null, params);
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  getWithQuery2<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ) {
    // let normalizeObject: { [param: string]: string | string[] } = {};
    // this.normalizeParamsObject(body, normalizeObject);
    const params = new HttpParams({ fromObject: body });
    let httpOptions = this.generateRequestOptions(null, params);
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  getFileWithQuery<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ) {
    let normalizeObject: { [param: string]: string | string[] } = {};
    this.normalizeParamsObject(body, normalizeObject);
    const params = new HttpParams({ fromObject: normalizeObject });
    let httpOptions = this.generateRequestOptions(null, params);
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    httpOptions['responseType'] = 'blob';
    // httpOptions['observe'] = 'response';

    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  getFileWithQuery2<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let normalizeObject: { [param: string]: string | string[] | any } = {};
    this.normalizeParamsObject(body, normalizeObject);
    const params = new HttpParams({ fromObject: normalizeObject });
    let httpOptions: any = this.generateRequestOptions(null, params);
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    httpOptions['responseType'] = 'blob' as 'json';
    httpOptions['observe'] = 'response';

    return this.http.get<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  post<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http
      .post<T>(url, body, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  postForm<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    let formBody = new FormData();
    if (body.files) {
      if (body.files.length) {
        for (let i = 0; i < body.files.length; i++) {
          formBody.append('Files', body.files[i], body.files[i].name);
        }
      } else {
        formBody.append('Files', body.files, body.files.name);
      }
    }
    let objectKeys = Object.keys(body);
    objectKeys.forEach((key) => {
      if (Array.isArray(body[key])) {
        if (body[key].length === 0) {
        } else if (key.toLocaleLowerCase() !== 'files') {
          body[key].forEach((item: any) => {
            formBody.append(key.charAt(0).toUpperCase() + key.slice(1), item);
          });
        }
      } else if (key.toLocaleLowerCase() !== 'files' && body[key] !== null) {
        formBody.append(key.charAt(0).toUpperCase() + key.slice(1), body[key]);
      }
    });
    return this.http
      .post<T>(url, formBody, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  postWithForm<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    const formBody = this.objectToFormData(body, null, null);
    return this.http
      .post<T>(url, formBody, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  postWithoutLoading<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions({
      'x-NotShowLoading': 'true',
    });
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http
      .post<T>(url, body, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  delete<T>(
    url: string,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.delete<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  deletWithParams<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ) {
    let normalizeObject: { [param: string]: string | string[] } = {};
    this.normalizeParamsObject(body, normalizeObject);
    const params = new HttpParams({ fromObject: normalizeObject });
    let httpOptions = this.generateRequestOptions(null, params);
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.delete<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  deletWithBody<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ) {
    let httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: body,
    };
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.delete<T>(url, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }

  put<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    return this.http.put<T>(url, body, httpOptions).pipe(
      catchError((err) => {
        return this.handleError(err);
      }) // then handle the error
    );
  }
  putForm<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    let formBody = new FormData();
    if (body.files) {
      if (body.files.length) {
        for (let i = 0; i < body.files.length; i++) {
          formBody.append('Files', body.files[i], body.files[i].name);
        }
      } else {
        formBody.append('Files', body.files, body.files.name);
      }
    }
    let objectKeys = Object.keys(body);
    objectKeys.forEach((key) => {
      if (Array.isArray(body[key])) {
        if (body[key].length === 0) {
        } else if (key.toLocaleLowerCase() !== 'files') {
          body[key].forEach((item: any) => {
            formBody.append(key.charAt(0).toUpperCase() + key.slice(1), item);
          });
        }
      } else if (key.toLocaleLowerCase() !== 'files' && body[key] !== null) {
        formBody.append(key.charAt(0).toUpperCase() + key.slice(1), body[key]);
      }
    });
    return this.http
      .put<T>(url, formBody, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  putWithForm<T>(
    url: string,
    body: any,
    isLoading: boolean = AppConfig.LOADING
  ): Observable<T> {
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    const formBody = this.objectToFormData(body, null, null);
    return this.http
      .put<T>(url, formBody, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  downloadFile(
    url: string,
    filename: string,
    isLoading: boolean = AppConfig.LOADING
  ): void {
    const baseUrl = url;
    let httpOptions = this.generateRequestOptions();
    httpOptions = this.generateRequestLoading(httpOptions, isLoading);
    httpOptions['responseType'] = 'blob' as 'json';
    this.http.get(baseUrl, httpOptions).subscribe((response: any) => {
      let dataType = response.type;
      let binaryData = [];
      binaryData.push(response);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(
        new Blob(binaryData, { type: dataType })
      );
      if (filename) downloadLink.setAttribute('download', filename);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  private generateRequestOptions(
    headerObject: any = null,
    paramsObject: any = null
  ): {
    headers: HttpHeaders;
    params?: HttpParams;
    responseType?: any;
    observe?: any;
  } {
    let token = '';
    // let token = this.helper.getLocalStorage("");
    let headers = new HttpHeaders();
    // headers = headers.set(
    //   'x-LngId',
    //   this.translate.currentLang == 'th' ? 1 + '' : 2 + ''
    // );
    // let now = new Date();
    if (!!token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      // this.helper.logout();
    }
    if (headerObject !== null && headerObject !== undefined) {
      let keys = Object.keys(headerObject);
      keys.forEach(
        (key) => (headers = headers.set(key, headerObject[key]))
        // (key) => (headers = headers.append(key, headerObject[key]))
        // (key) => (headers[key] = headerObject[key])
      );
    }
    let httpOptions: any = { headers };

    if (paramsObject !== null && headerObject !== undefined) {
      httpOptions['params'] = paramsObject;
    }
    return httpOptions;
  }

  private handleError(error: HttpErrorResponse): Observable<any> {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
      Swal.fire({
        icon: 'error',
        title: error.name ?? 'Error!',
        text: error.message,
      });
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
      Swal.fire({
        icon: 'error',
        title: error.error?.title ?? 'Error!',
        // html: error.error?.error ?? error.error,
        html:
          typeof error.error === 'string'
            ? error.error
            : error.error?.message ??
              error.error?.error ??
              'Please contact administrator.',
      });
    }
    if (error.status === 401) {
      // console.log(error?.error, ["ไม่มีสิทธิ์เข้าใช้งาน" , "You don't have permission to access."].includes(error.message));
      console.log(error?.error);

      const redirectURL = this.router.url;
      const appToken= this.common.getLocalStorage(LS_APP_TOKEN) ?? '';
      if (
        !!appToken &&
        AuthUtilService.isTokenExpired(appToken)
      ) {
        // this.authService.signOut();
        // Remove the access token from the local storage
        localStorage.removeItem(LS_ACCESS_TOKEN);
        localStorage.removeItem(LS_APP_TOKEN);
        localStorage.removeItem(LS_CURRENT_USER);
        localStorage.removeItem(LS_LAST_ACTIVITY);
        localStorage.removeItem(LS_USER_MENU);
        localStorage.removeItem(LS_USER_ROLE);
        localStorage.removeItem(LS_TOKEN_SCOPE);
        localStorage.removeItem(LS_APP_TOKEN_API);
        localStorage.removeItem(LS_REDIRECT_URL);
        localStorage.removeItem(LS_USER_GROUP);
        Swal.fire({
          icon: 'error',
          title: 'Session Expired!',
          text: 'Please sign in again.',
        }).then(() => {
          this.router.navigate(['sign-in'], {
            queryParams: { redirectURL },
          });
        });
        return EMPTY;
      }

      // debugger;
      // let token = this.getADToken();

      // //TODO
      // let expireToken = token?.account?.idTokenClaims?.exp;
      // if (token && expireToken) {
      //   let expire = new Date(expireToken * 1000);
      //   // let expire = new Date(new Date().getDate() + 1000);
      //   //console.log(expire);
      //   let current = new Date();
      //   if (current >= expire) {
      //     this.helper.refreshToken();
      //   }
      // }
      // else {
      //   this.helper.logOut();
      // }
      // if (!error?.error?.message) {
      //   // this.authorizationService.logout();
      // }
    }
    // Return an observable with a user-facing error message.
    // return throwError('Something bad happened; please try again later.');
    return throwError(error) as Observable<any>;
  }

  private normalizeParamsObject(obj: any, newObj: any, parentKey: string = '') {
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
        if (typeof obj[key] !== 'object') {
          newObj[parentKey + key] = obj[key];
        } else {
          if (obj[key] instanceof Array) {
            for (let index = 0; index < obj[key].length; index++) {
              const element = obj[key][index];
              if (typeof element !== 'object') {
                newObj[parentKey + key + `[${index}]`] = obj[key][index];
              } else
                this.normalizeParamsObject(
                  element,
                  newObj,
                  key + `[${index}].`
                );
            }
          } else {
            this.normalizeParamsObject(obj[key], newObj, key + '.');
          }
        }
      }
    });
  }

  private objectToFormData(obj: any, rootName: any, ignoreList: any) {
    var formData = new FormData();

    const appendFormData = (data: any, root: any) => {
      if (!ignore(root)) {
        root = root || '';
        console.log(typeof data);

        if (data instanceof File) {
          formData.append(root, data);
          // } else if (Array.isArray(data)) {
        } else if (data instanceof Array) {
          for (var i = 0; i < data.length; i++) {
            if (data[i] instanceof File) {
              formData.append(root, data[i]);
            } else {
              appendFormData(data[i], root + '[' + i + ']');
            }
          }
        } else if (typeof data === 'object' && data) {
          for (var key in data) {
            if (data.hasOwnProperty(key)) {
              if (root === '') {
                appendFormData(data[key], key);
              } else {
                appendFormData(data[key], root + '.' + key);
              }
            }
          }
        } else {
          if (data !== null && typeof data !== 'undefined' && data !== '') {
            formData.append(root, data);
          }
        }
      }
    };

    const ignore = (root: any) => {
      return (
        Array.isArray(ignoreList) &&
        ignoreList.some(function (x) {
          return x === root;
        })
      );
    };

    appendFormData(obj, rootName);

    return formData;
  }

  private generateRequestLoading(headerObject: any, isLoading: boolean) {
    if (isLoading) {
      if (headerObject.headers.has(HTTP_HEADER_NOT_LOADING)) {
        headerObject.headers = headerObject.headers.set(
          HTTP_HEADER_NOT_LOADING,
          String(!isLoading)
        );
      } else {
        headerObject.headers = headerObject.headers.append(
          HTTP_HEADER_NOT_LOADING,
          String(!isLoading)
        );
      }
    }
    return headerObject;
  }

  // getADToken(): any | null {
  //   if (this.storage[StorageConstant.AD_TOKEN])
  //     return JSON.parse(this.storage[StorageConstant.AD_TOKEN]);
  //   return null;
  // }
}
